import React, { useState } from "react";
import PropTypes from "prop-types";
import { pdfjs, Document, Page } from "react-pdf";
import { Box, Typography, CircularProgress } from "@mui/material";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

/**
 * ChecklistPDF component for displaying PDF documents
 * Uses react-pdf to render PDF files with zoom and download functionality
 */
const ChecklistPDF = ({ file, className = "", style = {} }) => {
  const [numPages, setNumPages] = useState(0);
  const [width, setWidth] = useState(800); // Will be updated based on container
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);

    // Auto-adjust width to fill container
    const containerElement = document.querySelector("#pdfContainer");
    if (containerElement) {
      const containerWidth = containerElement.clientWidth;
      setWidth(containerWidth - 20); // Leave small padding
    }
  };

  const onDocumentLoadError = (error) => {
    console.error("PDF loading error:", error);
    setError(error);
    setLoading(false);
  };

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        style={style}
        className={className}
      >
        <Typography color="error" variant="h6">
          Error loading PDF: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box style={style} className={className}>
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
          <Typography variant="body1" ml={2}>
            Loading PDF...
          </Typography>
        </Box>
      )}

      <Box id="pdfContainer" data-testid="pdfContainer">
        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={null} // We handle loading state ourselves
          noData={
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
            >
              <Typography variant="h6" color="textSecondary">
                No PDF document found
              </Typography>
            </Box>
          }
        >
          {Array.from(new Array(numPages), (el, index) => (
            <Page
              key={`page_${index + 1}`}
              width={width}
              pageNumber={index + 1}
              className="pdfPage"
              renderMode="svg"
              loading={null} // Disable individual page loading indicators
            />
          ))}
        </Document>
      </Box>

      {numPages > 0 && (
        <Box mt={2} textAlign="center">
          <Typography variant="body2" color="textSecondary">
            {numPages} page{numPages !== 1 ? "s" : ""}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

ChecklistPDF.propTypes = {
  file: PropTypes.oneOfType([
    PropTypes.string, // URL to PDF
    PropTypes.object, // File object
    PropTypes.instanceOf(ArrayBuffer), // ArrayBuffer
  ]).isRequired,
  title: PropTypes.string,
  showControls: PropTypes.bool,
  initialWidth: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default ChecklistPDF;
